// ---------------------------------------------------------------------------
// signupModal — vanilla port of the legacy signupModal.js + checkQueryString.js
// invite flow for the Download Center.
//
// Flow: plugin invite links arrive as `?flow_token=<token>&flow_hostname=<api>`.
// On load we persist those to sessionStorage (so the token survives navigation
// within the site). When a token is present the download buttons
// ([data-signup-trigger]) open a modal instead of navigating; the modal POSTs
// the visitor's email to `<apiRoot>/auth/invite/request/<token>` and surfaces
// sent / already-registered / error states.
//
// Graceful degradation: with no invite token in sessionStorage the modal is
// never wired up and every download button stays a plain link to its
// `data-download-href` (the seeded data points these at the public download
// page). There is NO hard-coded invite endpoint or credentials — the API root
// is only ever the runtime `flow_hostname` from the invite link. If the token
// exists but the request fails / there's no API root, the modal shows the error
// state, which carries a mailto fallback.
// ---------------------------------------------------------------------------

const QUERY_TOKEN_KEY = 'flow_token';
const QUERY_HOST_KEY = 'flow_hostname';
const SESSION_TOKEN_KEY = 'flowToken';
const SESSION_HOST_KEY = 'flow_hostname';
const EMAIL_ALREADY_EXISTS_CODE = 100006;

type ModalState = 'default' | 'loading' | 'sent' | 'exists' | 'error';

const safeSession = (): Storage | null => {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
};

// Mirror legacy checkQueryString: persist an invite token (min length guard) and
// API hostname from the URL query into sessionStorage.
const persistQueryString = (): void => {
	const store = safeSession();
	if (!store) return;
	const params = new URLSearchParams(window.location.search);
	const token = params.get(QUERY_TOKEN_KEY);
	if (token && token.length > 8) store.setItem(SESSION_TOKEN_KEY, token);
	const host = params.get(QUERY_HOST_KEY);
	if (host) store.setItem(SESSION_HOST_KEY, host);
};

const getToken = (): string => safeSession()?.getItem(SESSION_TOKEN_KEY) ?? '';
const getApiRoot = (): string => safeSession()?.getItem(SESSION_HOST_KEY) ?? '';

const setState = (inner: HTMLElement, state: ModalState): void => {
	inner.dataset.signupState = state;
	const email = inner.querySelector<HTMLInputElement>('input[type="email"]');
	const submit = inner.querySelector<HTMLButtonElement>('button[type="submit"]');
	const busy = state === 'loading' || state === 'sent' || state === 'exists';
	if (email) email.disabled = busy;
	if (submit) submit.disabled = busy;
};

const submitInvite = (inner: HTMLElement, email: string): void => {
	const token = getToken();
	const apiRoot = getApiRoot();
	if (!token || !apiRoot) {
		setState(inner, 'error');
		return;
	}

	setState(inner, 'loading');

	fetch(`${apiRoot}/auth/invite/request/${token}`, {
		method: 'POST',
		headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
		body: JSON.stringify({ email }),
	})
		.then((res) => {
			if (res.status === 200) {
				setState(inner, 'sent');
				return;
			}
			if (res.status === 400) {
				return res.json().then((body: { code?: number }) => {
					if (body && body.code === EMAIL_ALREADY_EXISTS_CODE) {
						setState(inner, 'exists');
					} else {
						setState(inner, 'error');
					}
				});
			}
			setState(inner, 'error');
		})
		.catch(() => setState(inner, 'error'));
};

const initModal = (modal: HTMLElement): void => {
	const inner = modal.querySelector<HTMLElement>('[data-signup-state]');
	const form = modal.querySelector<HTMLFormElement>('[data-signup-form]');
	if (!inner || !form) return;

	const open = (): void => {
		setState(inner, 'default');
		form.reset();
		modal.removeAttribute('hidden');
		modal.classList.add('is-open');
		const email = form.querySelector<HTMLInputElement>('input[type="email"]');
		email?.focus();
	};

	const close = (): void => {
		modal.classList.remove('is-open');
		modal.setAttribute('hidden', '');
	};

	for (const closer of modal.querySelectorAll<HTMLElement>('[data-signup-close]')) {
		closer.addEventListener('click', close);
	}
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
	});

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}
		const email = form.querySelector<HTMLInputElement>('input[type="email"]');
		if (!email) return;
		submitInvite(inner, email.value.trim());
	});

	// Only hijack download buttons when an invite token is present; otherwise the
	// buttons remain plain download links (no-JS / no-invite degradation).
	if (!getToken()) return;
	for (const trigger of document.querySelectorAll<HTMLElement>('[data-signup-trigger]')) {
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			open();
		});
	}
};

export const initSignupModal = (): void => {
	persistQueryString();
	for (const modal of document.querySelectorAll<HTMLElement>('[data-signup-modal]')) {
		initModal(modal);
	}
};
